import React, { useEffect, useRef, useState } from "react";
import { CloudOff } from "lucide-react";
import { toast } from "sonner";
import {
  getConnectivityState,
  subscribe,
} from "@/lib/connectivity";

/**
 * Slim slate banner shown directly below the global header when the app is
 * offline. Auto-hides when connectivity returns. Fires a single sonner toast
 * on each offline→online transition.
 */
export default function OfflineBanner() {
  const [state, setState] = useState(getConnectivityState());
  const wasOfflineRef = useRef(!getConnectivityState().online);

  useEffect(() => {
    const off = subscribe((next) => {
      setState(next);
      // Only toast on a real offline → online edge
      if (next.online && wasOfflineRef.current) {
        try {
          toast.success("Back online. Your data is safe.", {
            id: "rs-reconnect",
            duration: 3500,
          });
        } catch { /* sonner not mounted */ }
      }
      wasOfflineRef.current = !next.online;
    });
    return off;
  }, []);

  if (state.online) return null;

  return (
    <div
      data-testid="offline-banner"
      role="status"
      aria-live="polite"
      className="w-full border-b"
      style={{
        background: "rgba(148,163,184,0.12)",
        borderColor: "rgba(148,163,184,0.35)",
        color: "var(--rs-text-2)",
      }}
    >
      <div className="max-w-3xl mx-auto px-4 py-2 flex items-center gap-2.5">
        <CloudOff
          className="w-3.5 h-3.5 shrink-0"
          style={{ color: "var(--rs-amber)" }}
        />
        <p className="text-[11.5px] leading-snug">
          <span className="font-semibold">
            {state.reason === "simulated" ? "Simulated offline" : "Offline mode"}
          </span>{" "}
          — changes saved locally.
          {state.reason === "simulated" ? "" : " Will sync when connection returns."}
        </p>
      </div>
    </div>
  );
}
